import { roadmakerApi } from "./roadmakerApi";
import {
  isImageChecksum,
  mediaName,
  normalizeMediaName,
  orderMediaByMainImage,
} from "../domain/mainImage";
import { createHash } from "crypto-browserify";

// File d'uploads médias en attente par POI local (ADR 0005). Un média ne
// peut être envoyé au serveur qu'une fois le POI créé côté base (serverId
// connu) : les pièces choisies dans le formulaire sont donc gardées en
// mémoire jusqu'à la sauvegarde du POI, puis envoyées par flushPoiMediaUploads.
// Un même contenu (type + checksum du fichier) n'est mis en file qu'une fois.
const pendingUploads = new Map();
// Lot détaché de la file pendant un flush, gardé pour l'aperçu local.
const inFlightUploads = new Map();

const isKnownMediaType = (mediaType) =>
  ["image", "video", "audio", "icon", "qcm"].includes(mediaType);

// Empreinte du contenu réel : deux fichiers différents de même nom et
// même taille ne doivent pas être confondus (revue du 2026-09-04).
//
// F5 (revue du 01/10) : sur le banc public en HTTP (contexte non
// sécurisé), crypto.subtle est INDISPONIBLE — le SHA-256 doit alors passer
// par le fallback crypto-browserify (déjà dépendance directe, configurée
// en fallback webpack dans config-overrides.js). Chaîne complète :
//   1. file.arrayBuffer() quand le navigateur l'expose ;
//   2. sinon FileReader.readAsArrayBuffer (jsdom n'expose pas arrayBuffer
//      mais lit réellement le contenu) ;
//   3. crypto.subtle.digest("SHA-256", …) en contexte sécurisé ;
//   4. createHash("sha256") de crypto-browserify sinon.
// Le résultat est TOUJOURS le SHA-256 hex minuscule du contenu — identique
// au checksum calculé par le backend une fois publié. Seul un fichier
// réellement illisible (ni arrayBuffer ni FileReader) produit une clé
// opaque unique, qui ne ressemble pas à un checksum : l'appelant l'ignore
// proprement via isImageChecksum.
const readBytesViaFileReader = (file) =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () =>
        reject(reader.error || new Error("Lecture impossible"));
      reader.readAsArrayBuffer(file);
    } catch (error) {
      reject(error);
    }
  });

const toHex = (digest) =>
  [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

let computeContentKey = async (file) => {
  let bytes = null;
  if (typeof file?.arrayBuffer === "function") {
    bytes = await file.arrayBuffer();
  } else if (typeof FileReader === "function") {
    bytes = await readBytesViaFileReader(file);
  }
  if (!bytes) {
    return `opaque-${Date.now()}-${Math.random()}`;
  }
  if (typeof crypto !== "undefined" && crypto.subtle) {
    return toHex(await crypto.subtle.digest("SHA-256", bytes));
  }
  return createHash("sha256").update(new Uint8Array(bytes)).digest("hex");
};

const defaultComputeContentKey = computeContentKey;

export const __setContentKeyComputerForTests = (fn) => {
  computeContentKey = fn || defaultComputeContentKey;
};

// Clé de contenu (SHA-256 hex) d'un fichier, sans le mettre en file : sert
// à retrouver l'identité d'un contenu que queuePoiMediaUpload a signalé
// comme doublon (retour null).
export const computePoiMediaContentKey = (file) => computeContentKey(file);

export const queuePoiMediaUpload = async (
  markerLocalId,
  mediaType,
  file,
  { replaces } = {}
) => {
  if (!isKnownMediaType(mediaType)) {
    throw new Error("Type de média inconnu.");
  }
  if (!file) {
    throw new Error("Fichier média requis.");
  }
  const contentKey = await computeContentKey(file);
  const queue = pendingUploads.get(markerLocalId) || [];
  const duplicate = queue.some(
    (item) => item.mediaType === mediaType && item.contentKey === contentKey
  );
  if (duplicate) {
    return null;
  }
  const item = {
    id: `${mediaType}-${Date.now()}-${queue.length}`,
    mediaType,
    file,
    contentKey,
    replaces,
  };
  pendingUploads.set(markerLocalId, [...queue, item]);
  // Lot « image principale » (2026-10-01) : la clé de contenu EST
  // l'identité stable du média — SHA-256 hex identique au checksum
  // backend une fois publié. La retourner permet au formulaire de
  // désigner l'image comme principale AVANT la sauvegarde. Sous jsdom
  // (sans lecture de contenu), la clé opaque ne ressemble pas à un
  // checksum : l'appelant l'ignore alors proprement via isImageChecksum.
  return contentKey;
};

export const pendingPoiMediaCount = (markerLocalId) =>
  (pendingUploads.get(markerLocalId) || []).length;

// Constat f10 (revue finale du 01/10) : lecture DEFENSIVE de la file d'un
// POI (copie) — MarkerImage s'en sert pour distinguer une entrée legacy
// réellement sans pièce en file (non supprimable : elle reviendrait au
// rechargement) d'un aperçu local encore en attente d'upload.
export const pendingPoiMediaUploadsSnapshot = (markerLocalId) =>
  [...(pendingUploads.get(markerLocalId) || [])];

// Aperçu local d'une pièce encore en file (vidéo, audio) : sans lui, le
// lecteur pointait vers l'URL GitHub historique reconstruite, qui n'existe
// pas, et rien ne s'affichait avant la sauvegarde. `matches` est le même
// prédicat (item) => booléen que pour discardPoiMediaUploads. La pièce est
// cherchée dans la file ET dans le lot en cours d'envoi : sinon l'aperçu
// disparaissait le temps du flush. L'URL objet est créée à la demande,
// mise en cache sur la pièce et révoquée quand la pièce est retirée du
// formulaire. Après un envoi réussi elle n'est pas révoquée : un lecteur
// encore ouvert peut la lire, et son coût se limite à la pièce déjà en
// mémoire. Retourne l'URL, ou undefined si aucune pièce ne correspond.
export const pendingPoiMediaPreview = (markerLocalId, mediaType, matches) => {
  if (
    typeof matches !== "function" ||
    typeof URL === "undefined" ||
    !URL.createObjectURL
  ) {
    return undefined;
  }
  const item = [
    ...(pendingUploads.get(markerLocalId) || []),
    ...(inFlightUploads.get(markerLocalId) || []),
  ].find((candidate) => candidate.mediaType === mediaType && matches(candidate));
  if (!item) return undefined;
  if (!item.previewUrl) {
    item.previewUrl = URL.createObjectURL(item.file);
  }
  return item.previewUrl;
};

const revokePreview = (item) => {
  if (item.previewUrl && typeof URL !== "undefined" && URL.revokeObjectURL) {
    URL.revokeObjectURL(item.previewUrl);
  }
};

// Vide la file après création ou mise à jour réussie du POI. Chaque pièce
// passe par le contrat poiMedia (POST binaire). Un échec n'interrompt pas
// les autres pièces et l'opération concernée reste dans la file pour un
// prochain flush. Si le POI n'a pas encore de serverId, la file est aussi
// conservée pour une prochaine sauvegarde.
export const flushPoiMediaUploads = async (
  markerLocalId,
  projectId,
  serverId,
  api = roadmakerApi
) => {
  const queue = pendingUploads.get(markerLocalId) || [];
  if (!serverId || queue.length === 0) {
    return [];
  }
  // Détacher le lot traité pour laisser un nouveau choix utilisateur entrer
  // dans une file distincte pendant les appels réseau.
  pendingUploads.set(markerLocalId, []);
  inFlightUploads.set(markerLocalId, queue);
  const results = [];
  const retries = [];
  for (const item of queue) {
    try {
      const media =
        item.uploadedMedia ||
        (await api.poiMedia.upload(
          projectId,
          serverId,
          item.mediaType,
          item.file
        ));
      let replacementError;
      if (item.replaces && item.replaces.fileName !== media.fileName) {
        try {
          await api.poiMedia.remove(projectId, serverId, item.replaces);
        } catch (error) {
          console.error("Échec de la suppression du média remplacé :", error);
          replacementError = error;
          retries.push({ ...item, uploadedMedia: media });
        }
      }
      results.push({ ...item, media, replacementError });
    } catch (error) {
      console.error("Échec de l'upload média POI :", error);
      results.push({ ...item, error });
      retries.push(item);
    }
  }
  inFlightUploads.delete(markerLocalId);
  const newlyQueued = pendingUploads.get(markerLocalId) || [];
  if (newlyQueued.length || retries.length) {
    pendingUploads.set(markerLocalId, [...retries, ...newlyQueued]);
  } else {
    pendingUploads.delete(markerLocalId);
  }
  return results;
};

// Vue « bilan de sauvegarde » des résultats d'un flush : `flushPoiMediaUploads`
// ne LÈVE JAMAIS d'exception — un échec d'envoi devient un résultat porteur de
// `error`, un échec de nettoyage du média remplacé un `replacementError`. Les
// deux comptent, car dans le second cas l'ancien média est TOUJOURS publié : la
// suppression est simplement en attente d'un prochain flush. L'appelant qui
// n'examine pas ce retour croit à un succès complet (défaut relevé par la revue
// du 15 septembre 2026) : ce sélecteur rend le contrôle explicite et testable.
export const selectPoiMediaUploadFailures = (results) => {
  const failures = [];
  for (const result of results || []) {
    if (result.error) {
      failures.push({ kind: "upload", result, error: result.error });
    } else if (result.replacementError) {
      failures.push({
        kind: "replacement",
        result,
        error: result.replacementError,
      });
    }
  }
  return failures;
};

// Applique les résultats d'un flush au DTO local. Une entrée remplacée n'est
// retirée qu'après son DELETE réussi : en cas d'échec de nettoyage, les deux
// médias restent visibles afin que l'utilisateur puisse réessayer sans perte.
export const mergePoiMediaUploadResults = (currentMedia, results) => {
  const byKey = new Map(
    (currentMedia || []).map((media) => [
      `${media.mediaType}:${media.fileName}`,
      media,
    ])
  );
  for (const result of results || []) {
    if (!result.media) continue;
    if (
      result.replaces &&
      !result.replacementError &&
      result.replaces.fileName !== result.media.fileName
    ) {
      byKey.delete(`${result.replaces.mediaType}:${result.replaces.fileName}`);
    }
    byKey.set(
      `${result.media.mediaType}:${result.media.fileName}`,
      result.media
    );
  }
  return [...byKey.values()];
};

// Compare un nom de fichier sans tenir compte des variations de formatage
// historiques : la preview locale porte le nom NORMALISÉ par
// normalizeFileName (accents retirés, ponctuation → _) tandis que le média
// publié porte l'originalName brut — historiquement même mojibake
// (« FacultÃ© » : double encodage Latin-1/UTF-8 des anciens uploads).
// Seuls les caractères alphanumériques sont comparés : accents, espaces,
// ponctuation et artefacts d'encodage deviennent indifférents (retests du
// 2026-09-04 : virgule, puis mojibake).
// Constat f14 (revue finale du 01/10) : délègue au helper EXPORTÉ du
// domaine (normalizeMediaName) — la copie privée canonical est retirée.
const sameDisplayName = (left, right) => {
  if (typeof left !== "string" || typeof right !== "string") return false;
  return normalizeMediaName(left) === normalizeMediaName(right);
};

// C1 (parcours 2026-09-04) : après la sauvegarde du projet, l'écran de
// consultation restait sur son rendu périmé. Ce helper reconstruit le
// marqueur avec les médias serveur fraîchement publiés ET retire des
// champs d'affichage legacy (image/video/audio + noms) les entrées locales
// construites depuis les URL GitHub historiques — ces fichiers n'ont jamais
// existé sur GitHub, les lecteurs correspondants sont morts. Retourne
// null si rien ne change (aucun re-rendu inutile).
export const applyFlushedMediaToMarker = (marker, results) => {
  const published = (results || []).filter((result) => result.media);
  if (published.length === 0) return null;

  const publishedByType = new Map();
  for (const { media } of published) {
    publishedByType.set(media.mediaType, [
      ...(publishedByType.get(media.mediaType) || []),
      media,
    ]);
  }

  const nextMedia = mergePoiMediaUploadResults(marker.media, results);
  const nextMarker = {
    ...marker,
    // ORDRE de lecture (lot « image principale » 2026-10-01) : miroir local
    // du tri du DTO backend — l'image active désignée par mainImage passe en
    // tête pour que la consultation soit correcte sans rechargement.
    media: orderMediaByMainImage(nextMedia, marker.mainImage),
  };
  // Défaut « flush partiel » (4e revue du 01/10) : le cache client-only
  // mainImageIds est RECONSTRUIT après la boucle ci-dessous (voir
  // rebuildFlushedMainImageIds) — le supprimer rendait une pièce RESTÉE en
  // file (envoi échoué) indistinguable d'une entrée legacy à la
  // réouverture : sa suppression n'abandonnait plus la pièce et elle
  // fuyait vers un upload ultérieur.

  // Index des previews IMAGE retirées (publiées avec succès) : conservés
  // pour la reconstruction du cache, alignés sur les indexes des noms.
  const imageMatchedIndexes = new Set();

  for (const mediaType of ["image", "video", "audio"]) {
    const typeResults = publishedByType.get(mediaType) || [];
    if (typeResults.length === 0) continue;
    const valuesField = mediaType;
    const namesField = `${mediaType}Name`;
    const legacyValues = marker[valuesField];
    const legacyNames = marker[namesField];
    if (!Array.isArray(legacyValues)) continue;

    // Index des entrées legacy correspondant à un média publié : la
    // preview locale est remplacée par l'URL authentifiée du serveur.
    const matchedIndexes = new Set();
    for (let index = 0; index < legacyValues.length; index += 1) {
      const name = Array.isArray(legacyNames) ? legacyNames[index] : undefined;
      const match = typeResults.find(
        (media) =>
          sameDisplayName(name, media.originalName) ||
          sameDisplayName(name, media.fileName)
      );
      if (match) matchedIndexes.add(index);
    }
    if (mediaType === "image") {
      for (const index of matchedIndexes) imageMatchedIndexes.add(index);
    }

    const keptValues = legacyValues.filter(
      (_, index) => !matchedIndexes.has(index)
    );
    const keptNames = Array.isArray(legacyNames)
      ? legacyNames.filter((_, index) => !matchedIndexes.has(index))
      : legacyNames;

    nextMarker[valuesField] = keptValues;
    nextMarker[namesField] = keptNames;
  }

  rebuildFlushedMainImageIds(nextMarker, marker, imageMatchedIndexes);

  return nextMarker;
};

// Défaut « flush partiel » (4e revue du 01/10) : reconstruction du cache
// client-only mainImageIds sur le marqueur republié après flush. Contrat —
// exactement l'état que la réouverture reconstruirait :
// 1. partie formulaire : les premiers `previousMarker.imageName.length` ids
//    de l'ANCIEN cache (alignés sur les ANCIENS noms), filtrés des previews
//    retirées (publiées avec succès, `matchedIndexes`) — une pièce RESTÉE en
//    file y garde son contentKey exact ;
// 2. partie serveur : checksums EXACTS des images actives (status ≠
//    "deleting", url présente) de `nextMarker.media` trié, dans cet ordre.
// Le cache n'est posé que s'il reste ALIGNÉ (un id par nom restant, partie
// serveur en checksums exacts, ids tous chaînes) — sinon il est supprimé :
// la réouverture retombera sur buildInitialImageIds plutôt qu'un tableau
// désaligné. AUCUN matching par nom ici : les ids suivent les INDEX.
const rebuildFlushedMainImageIds = (
  nextMarker,
  previousMarker,
  matchedIndexes
) => {
  const previousIds = Array.isArray(previousMarker.mainImageIds)
    ? previousMarker.mainImageIds
    : null;
  if (!previousIds) {
    delete nextMarker.mainImageIds;
    return;
  }
  const previousNames = Array.isArray(previousMarker.imageName)
    ? previousMarker.imageName
    : [];
  const formPart = previousIds
    .slice(0, previousNames.length)
    .filter((_, index) => !matchedIndexes.has(index));
  const serverImages = (nextMarker.media || []).filter(
    (media) =>
      media &&
      media.mediaType === "image" &&
      media.status !== "deleting" &&
      media.url
  );
  const serverIds = serverImages.map((media) => media.checksum);
  const keptNames = Array.isArray(nextMarker.imageName)
    ? nextMarker.imageName
    : [];
  const aligned =
    formPart.length === keptNames.length &&
    formPart.every((id) => typeof id === "string") &&
    serverIds.every((id) => isImageChecksum(id));
  if (aligned) {
    nextMarker.mainImageIds = [...formPart, ...serverIds];
  } else {
    delete nextMarker.mainImageIds;
  }
};

export const deletePoiMediaForMarker = async (
  marker,
  mediaType,
  api = roadmakerApi
) => {
  if (!isKnownMediaType(mediaType)) {
    throw new Error("Type de média inconnu.");
  }

  const discardPendingMedia = () =>
    marker?.id
      ? discardPoiMediaUploads(
          marker.id,
          (item) => item.mediaType === mediaType
        )
      : 0;
  const knownPersistedMedia = (marker?.media || []).some(
    (media) => media.mediaType === mediaType
  );

  if (!marker?.serverId || !marker?.Project_id) {
    return discardPendingMedia() > 0;
  }

  let selectedMedia;
  try {
    const persistedMedia = await api.poiMedia.list(
      marker.Project_id,
      marker.serverId
    );
    selectedMedia = persistedMedia.filter(
      (media) => media.mediaType === mediaType
    );
    await Promise.all(
      selectedMedia.map((media) =>
        api.poiMedia.remove(marker.Project_id, marker.serverId, media)
      )
    );
  } catch (error) {
    console.error("Échec de la suppression groupée des médias serveur :", error);
    return false;
  }

  const discardedPendingMedia = discardPendingMedia();
  if (!selectedMedia.length && !knownPersistedMedia && !discardedPendingMedia) {
    return false;
  }

  marker.media = (marker.media || []).filter(
    (media) => media.mediaType !== mediaType
  );
  return true;
};

// Retire de la file les pièces supprimées du formulaire avant sauvegarde.
// matches est un prédicat (item) => booléen ; renvoie le nombre de retraits.
export const discardPoiMediaUploads = (markerLocalId, matches) => {
  const queue = pendingUploads.get(markerLocalId) || [];
  if (queue.length === 0 || typeof matches !== "function") {
    return 0;
  }
  const remaining = queue.filter((item) => !matches(item));
  queue
    .filter((item) => !remaining.includes(item))
    .forEach(revokePreview);
  pendingUploads.set(markerLocalId, remaining);
  return queue.length - remaining.length;
};

// Repubblier le marqueur dans le state global après une mutation de
// `marker.media` faite par référence (suppression serveur réussie). Sans
// ce republicage, la consultation garde son rendu périmé jusqu'au
// rechargement du parcours (constats du parcours 2026-09-04 : doublons
// audio/vidéo persistant pendant la navigation entre POIs).
// setMarkers est le setter du tableau `markers` ; la fonction est no-op
// s'il est absent (composants de consultation sans accès au state).
export const republishMarkerMedia = (marker, setMarkers) => {
  if (typeof setMarkers !== "function" || !marker) return false;
  setMarkers((previousMarkers) =>
    (previousMarkers || []).map((previous) =>
      previous.id === marker.id ? { ...previous, media: [...(marker.media || [])] } : previous
    )
  );
  return true;
};

// F4d (retest 3 du 2026-09-04) : le formulaire d'édition initialise ses
// états locaux avec les URLs des médias serveur (affichage) ; « sauvegarder »
// le POI recopiait ces URLs dans les champs legacy (marker.video/audio/image
// + noms) → la consultation affichait le média deux fois (champ legacy ET
// marker.media) jusqu'au rechargement du parcours. Ce helper retire des
// champs legacy toute valeur correspondant à un média serveur publié :
// les médias normalisés vivent UNIQUEMENT dans marker.media. Retourne le
// même marqueur si rien ne change.
export const stripServerMediaFromLegacyFields = (marker) => {
  if (!marker) return marker;
  const serverUrlsByType = new Map();
  const serverNamesByType = new Map();
  for (const media of marker.media || []) {
    if (!media || !media.url) continue;
    serverUrlsByType.set(
      media.mediaType,
      (serverUrlsByType.get(media.mediaType) || []).concat(media.url)
    );
    const displayName = mediaName(media);
    serverNamesByType.set(
      media.mediaType,
      (serverNamesByType.get(media.mediaType) || []).concat(displayName)
    );
  }

  let changed = false;
  const next = { ...marker };
  for (const mediaType of ["image", "video", "audio"]) {
    const urls = serverUrlsByType.get(mediaType) || [];
    const names = serverNamesByType.get(mediaType) || [];
    if (urls.length === 0) continue;
    const values = marker[mediaType];
    const valueNames = marker[`${mediaType}Name`];
    if (!Array.isArray(values) && !Array.isArray(valueNames)) continue;
    // Défaut « alignement strip » (4e revue du 01/10) : UN SEUL ensemble
    // d'indices pour les valeurs ET les noms — un index sort s'il porte
    // une URL serveur (côté valeur) OU un nom/URL serveur (côté nom). Les
    // deux filtres indépendants désalignaient les tableaux (homonymes,
    // croisement URL/nom) : la paire recomposée appariait ensuite des
    // entrées étrangères. Comportement préservé quand un seul tableau
    // existe : seul son prédicat propre alimente l'ensemble.
    const removedIndexes = new Set();
    if (Array.isArray(values)) {
      values.forEach((value, index) => {
        if (urls.includes(value)) removedIndexes.add(index);
      });
    }
    if (Array.isArray(valueNames)) {
      valueNames.forEach((name, index) => {
        if (
          names.some((serverName) => serverName === name) ||
          urls.some((url) => url === name)
        ) {
          removedIndexes.add(index);
        }
      });
    }
    if (Array.isArray(values)) {
      const kept = values.filter((_, index) => !removedIndexes.has(index));
      if (kept.length !== values.length) {
        next[mediaType] = kept;
        changed = true;
      }
    }
    if (Array.isArray(valueNames)) {
      const keptNames = valueNames.filter(
        (_, index) => !removedIndexes.has(index)
      );
      if (keptNames.length !== valueNames.length) {
        next[`${mediaType}Name`] = keptNames;
        changed = true;
      }
    }
  }
  return changed ? next : marker;
};

// Images de QCM image (ADR 0005, amendement 2026-10-23) : une réponse est
// stockée dans answersArray sous la forme `poimedia:qcm:<sha256>`. La
// référence peut être écrite AVANT l'upload (différé à la sauvegarde) : le
// SHA-256 est la clé de contenu de la file, identique au checksum serveur.
const QCM_REFERENCE_PREFIX = "poimedia:qcm:";

export const qcmReferenceFor = (checksum) =>
  `${QCM_REFERENCE_PREFIX}${checksum}`;

// Checksum d'une référence `poimedia:qcm:<sha256>`, sinon null (valeur
// legacy, case vide ou référence malformée).
export const parseQcmReference = (value) => {
  if (typeof value !== "string" || !value.startsWith(QCM_REFERENCE_PREFIX)) {
    return null;
  }
  const checksum = value.slice(QCM_REFERENCE_PREFIX.length);
  return isImageChecksum(checksum) ? checksum : null;
};

// Aperçus locaux (object URL) des fichiers en file : un par fichier, créé à
// la demande. Jamais écrits dans l'état sauvegardé.
const previewUrls = new WeakMap();
const previewUrlOf = (file) => {
  if (!file || typeof URL === "undefined") return "";
  if (previewUrls.has(file)) return previewUrls.get(file);
  if (typeof URL.createObjectURL !== "function") return "";
  try {
    const objectUrl = URL.createObjectURL(file);
    previewUrls.set(file, objectUrl);
    return objectUrl;
  } catch (error) {
    return "";
  }
};

// Résout une valeur d'answersArray en URL d'affichage :
// - "" / absent : rien ("") ;
// - référence : aperçu local si le fichier est encore en file, sinon URL
//   authentifiée du média `qcm` serveur de même checksum (status ≠ deleting),
//   sinon "" (média pas encore disponible) ;
// - toute autre valeur (data URL, URL http(s) historique) : telle quelle.
export const resolveQcmImageSource = (marker, value) => {
  if (typeof value !== "string" || value === "") return "";
  if (value.startsWith(QCM_REFERENCE_PREFIX)) {
    const checksum = parseQcmReference(value);
    if (!checksum) return "";
    const pending = (pendingUploads.get(marker?.id) || []).find(
      (item) => item.mediaType === "qcm" && item.contentKey === checksum
    );
    if (pending) {
      const preview = previewUrlOf(pending.file);
      if (preview) return preview;
    }
    const serverMedia = (marker?.media || []).find(
      (media) =>
        media &&
        media.mediaType === "qcm" &&
        media.checksum === checksum &&
        media.status !== "deleting" &&
        media.url
    );
    return serverMedia ? serverMedia.url : "";
  }
  return value;
};

// Nettoyage après une sauvegarde réussie (flush terminé) : supprime côté
// serveur les médias `qcm` du POI dont le checksum n'est plus référencé par
// aucun answersArray et qui ne sont plus en file (réponse remplacée, question
// supprimée). Un échec de suppression est journalisé et ne bloque rien.
// Sans qcmImageArray exploitable, rien n'est supprimé (garde-fou).
// Retourne les médias effectivement supprimés ; marker.media est mis à jour.
export const cleanupOrphanQcmMedia = async (
  marker,
  projectId,
  api = roadmakerApi
) => {
  if (!marker?.serverId || !projectId || !Array.isArray(marker.qcmImageArray)) {
    return [];
  }
  const referenced = new Set();
  for (const item of marker.qcmImageArray) {
    for (const answer of item?.answersArray || []) {
      const checksum = parseQcmReference(answer);
      if (checksum) referenced.add(checksum);
    }
  }
  const pending = new Set(
    (pendingUploads.get(marker.id) || [])
      .filter((item) => item.mediaType === "qcm")
      .map((item) => item.contentKey)
  );
  const orphans = (marker.media || []).filter(
    (media) =>
      media &&
      media.mediaType === "qcm" &&
      media.status !== "deleting" &&
      !referenced.has(media.checksum) &&
      !pending.has(media.checksum)
  );
  const removed = [];
  for (const media of orphans) {
    try {
      await api.poiMedia.remove(projectId, marker.serverId, media);
      removed.push(media);
    } catch (error) {
      console.error("Échec de la suppression d'une image de QCM orpheline :", error);
    }
  }
  if (removed.length > 0) {
    marker.media = (marker.media || []).filter(
      (media) => !removed.includes(media)
    );
  }
  return removed;
};
